import { z } from "zod";

export const luhnCheck = (cardNumber) => {
  const digits = cardNumber.replace(/\D/g, "");
  if (digits.length === 0) return false;

  let sum = 0;
  let isEven = false;

  for (let i = digits.length - 1; i >= 0; i--) {
    let digit = parseInt(digits[i], 10);

    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
};

export const getCardType = (cardNumber) => {
  const digits = cardNumber.replace(/\D/g, "");

  if (!luhnCheck(cardNumber)) {
    return "unknown";
  }

  if (digits.length === 15) {
    return "amex";
  } else if (digits.length === 16) {
    return "other";
  }

  return "unknown";
};

export const isCardHolderNameValid = (val) => {
  const trimmed = val.trim();
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length < 2) return false;
  return words.every((w) => /^[\p{L}]+$/u.test(w));
};

export const createCardSchema = (t, currentCardNumber = "", showCardHolder) => {
  const cardType = getCardType(currentCardNumber);

  return z.object({
    cardNumber: z
      .string()
      .min(1, t("common.creditCardFormErrorCardNumberRequired"))
      .refine((val) => {
        const unmasked = val.replace(/\D/g, "");
        return unmasked.length >= 13 && unmasked.length <= 19;
      }, t("common.creditCardFormErrorCardNumberLength"))
      .refine(
        (val) => luhnCheck(val),
        t("common.creditCardFormErrorCardNumberInvalid"),
      ),
    expiry: z
      .string()
      .min(1, t("common.creditCardFormErrorExpiryRequired"))
      .regex(/^\d{2}\/\d{2}$/, t("common.creditCardFormErrorExpiryFormat"))
      .refine((val) => {
        const [month, year] = val.split("/").map(Number);
        if (month < 1 || month > 12) return false;

        const now = new Date();
        const currentYear = now.getFullYear() % 100;
        const currentMonth = now.getMonth() + 1;

        if (year < currentYear) return false;
        if (year === currentYear && month < currentMonth) return false;

        return true;
      }, t("common.creditCardFormErrorExpiryPast")),
    cvv: z
      .string()
      .min(1, t("common.creditCardFormErrorCVVRequired"))
      .refine(
        (val) => {
          if (cardType === "amex") {
            return val.replace(/\D/g, "").length === 4;
          } else if (cardType === "other") {
            return val.replace(/\D/g, "").length === 3;
          }
          const cvvLength = val.replace(/\D/g, "").length;
          return cvvLength === 3 || cvvLength === 4;
        },
        cardType === "amex"
          ? t("common.creditCardFormErrorCVVLengthAmex")
          : cardType === "other"
            ? t("common.creditCardFormErrorCVVLengthOther")
            : t("common.creditCardFormErrorCVVLengthGeneric"),
      ),
    ...(showCardHolder && {
      name: z
        .string()
        .min(1, t("common.creditCardFormErrorCardHolderRequired"))
        .refine(
          (val) => isCardHolderNameValid(val),
          t("common.creditCardFormErrorCardHolderInvalid"),
        ),
    }),
  });
};
